import type { ReactNode } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { ACCOUNT_ACCENT } from './accountStyles';

export type AutoPurchaseHelpTopic = 'buyer' | 'renter' | 'clearance';

const COPY: Record<AutoPurchaseHelpTopic, { title: string; body: ReactNode }> =
  {
    buyer: {
      title: 'Auto-purchase buyer leads',
      body: (
        <div className="space-y-3 text-sm leading-relaxed text-slate-700">
          <p>
            When this is <strong>on</strong>, we automatically debit your wallet
            and assign <strong>fresh buyer leads</strong> that match your
            territory as soon as they become available, using your standard
            buyer lead price.
          </p>
          <p>
            When it is <strong>off</strong>, buyer leads are not auto-assigned;
            you can still buy manually from Premium or other flows if you
            choose.
          </p>
          <p className="text-xs text-slate-500">
            Requires a positive wallet balance and a saved territory. Respects
            leads hidden from routing or flagged as low quality.
          </p>
        </div>
      )
    },
    renter: {
      title: 'Auto-purchase tenant leads',
      body: (
        <div className="space-y-3 text-sm leading-relaxed text-slate-700">
          <p>
            When this is <strong>on</strong>, we automatically debit your wallet
            and assign <strong>fresh tenant (rental seeker) leads</strong> in
            your territory at your tenant lead price.
          </p>
          <p>
            When it is <strong>off</strong>, tenant leads are not auto-assigned
            from the fresh pool.
          </p>
          <p className="text-xs text-slate-500">
            Same balance and territory rules as buyer auto-purchase. Hidden /
            flagged leads are not routed.
          </p>
        </div>
      )
    },
    clearance: {
      title: 'Auto-purchase Clearance leads',
      body: (
        <div className="space-y-3 text-sm leading-relaxed text-slate-700">
          <p>
            <strong>What is Clearance?</strong> Clearance leads are older
            inventory (past the &quot;fresh&quot; window, up to 60 days) that
            did not sell at full price in the live routing pool. They are
            offered so you can still work the opportunity at a discount.
          </p>
          <p>
            <strong>Why 50% off?</strong> The listing has aged, so demand is
            typically lower. The reduced price reflects that you are buying
            later-cycle demand and may need more follow-up.
          </p>
          <p>
            <strong>Benefits:</strong> Lower cost per lead, automatic fills in
            your territory without watching Clearance manually, and a chance to
            convert leads others passed on.
          </p>
          <p>
            <strong>Risks:</strong> Older inquiries may be colder or already
            engaged elsewhere. You should expect potentially lower connect rates
            than fresh leads. Wallet is debited the same way as other
            auto-purchases—only enable if you are comfortable with that
            trade-off.
          </p>
          <p className="text-xs text-slate-500">
            Clearance auto-purchase only considers leads{' '}
            <strong>inside your saved territory</strong>. If both Buyer and
            Tenant auto-purchase are off but Clearance is on, both buyer and
            tenant Clearance leads in territory can still be purchased
            automatically at the discounted price.
          </p>
        </div>
      )
    }
  };

export function AutoPurchaseHelpTrigger({
  topic,
  openTopic,
  onOpenTopic
}: {
  topic: AutoPurchaseHelpTopic;
  openTopic: AutoPurchaseHelpTopic | null;
  onOpenTopic: (t: AutoPurchaseHelpTopic | null) => void;
}) {
  const open = openTopic === topic;
  const { title, body } = COPY[topic];

  return (
    <>
      <button
        type="button"
        className="inline-flex shrink-0 rounded-full p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#2b6cff]"
        aria-label={`Help: ${title}`}
        onClick={() => onOpenTopic(open ? null : topic)}
      >
        <HelpCircle className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="presentation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
            aria-label="Close dialog"
            onClick={() => onOpenTopic(null)}
          />
          <div
            className={`relative z-[101] max-h-[min(85vh,540px)] w-full max-w-lg overflow-y-auto rounded-xl p-5 shadow-xl ${ACCOUNT_ACCENT.cardSurface}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`autopurchase-help-${topic}-title`}
          >
            <div className="flex items-start justify-between gap-3">
              <h2
                id={`autopurchase-help-${topic}-title`}
                className="text-base font-semibold text-slate-900"
              >
                {title}
              </h2>
              <button
                type="button"
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                onClick={() => onOpenTopic(null)}
                aria-label="Close"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="mt-4">{body}</div>
            <button
              type="button"
              className="mt-5 w-full rounded-lg bg-slate-100 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-200"
              onClick={() => onOpenTopic(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
